'use client'

import { useEffect } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useRouter } from 'next/navigation' // Import useRouter từ Next.js
import { NewsResponse } from '@/models/news.model'
import { fetchNews } from '@/services/newsService'

export interface UseNewsParams {
  page?: number
  size?: number
  menuSlug?: string
}

const DEFAULT_PAGE_SIZE = 10

export const useNews = ({
  page = 1,
  size = DEFAULT_PAGE_SIZE,
  menuSlug = '',
}: UseNewsParams = {}) => {
  const router = useRouter() // Sử dụng router để điều hướng

  const queryKey = ['news', { menuSlug, size, page }]

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery<NewsResponse, Error>({
    queryKey,
    queryFn: () => fetchNews(page, size, menuSlug),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.page
      const totalPages = lastPage.totalPage
      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    getPreviousPageParam: (firstPage) => {
      const currentPage = firstPage.page
      return currentPage > 1 ? currentPage - 1 : undefined
    },
    initialPageParam: 1,
    refetchOnWindowFocus: false,
  })

  // Điều hướng đến trang NotFound nếu có lỗi
  useEffect(() => {
    if (isError) {
      router.push('/404')
    }
  }, [isError, router])

  const allNews = data?.pages.flatMap((page) => page.items) || []
  const totalPages = data?.pages[data?.pages.length - 1]?.totalPage || 1

  return {
    allNews,
    totalPages,
    isLoading,
    isError,
    error, // Trả về lỗi nếu cần sử dụng
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  }
}
